import { config } from '#~/config/config.js';
import { isElectronApp } from '#~/electron/utils.js';
import type { AppState, User as Auth0User } from '@auth0/auth0-react';
import { createAuthJourneyTracker } from '#~/analytics/domains/auth-journey.js';
import { track as trackAnalytics } from '#~/analytics/track.js';
import type {
  AnalyticsEvent,
  AuthJourneyStateTransport,
  AuthJourneySource
} from '#~/analytics/types/index.js';
import { ATTRIBUTION_PARAMS, PostHogManager } from '#~/posthog/index.js';
import type { LocationState } from './types.js';
import { captureSignupIdentity, readSignupAttribution } from './signup-posthog.js';
import type { User } from '@studio/models/user';

const ACTIVE_AUTH_JOURNEY_KEY = 'analytics.active_auth_journey';
const SIGNUP_ATTRIBUTION_SNAPSHOT_KEY = 'analytics.signup_attribution_snapshot';
const AUTH_JOURNEY_TERMINAL_KEY = 'analytics.auth_journey_terminal';
const LAST_KNOWN_USER_ID_KEY = 'analytics.last_known_user_id';
const DISABLE_HOSTED_PAGE_TRACKING_KEY = 'analytics.disable_hosted_page_tracking';
const ACTIVE_AUTH_JOURNEY_TTL_MS = 30 * 60 * 1000;
const AUTH_JOURNEY_SOURCES: ReadonlySet<AuthJourneySource> = new Set([
  'studio_login',
  'studio_register'
]);

const readStorage = (storage: Storage | undefined, key: string): string | undefined => {
  if (!storage) {
    return undefined;
  }

  try {
    return storage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const writeStorage = (storage: Storage | undefined, key: string, value: string): void => {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(key, value);
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
};

const removeStorage = (storage: Storage | undefined, key: string): void => {
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(key);
  } catch {
    // Ignore storage failures.
  }
};

const safeSessionStorage = (): Storage | undefined =>
  typeof window !== 'undefined' ? window.sessionStorage : undefined;

const safeLocalStorage = (): Storage | undefined =>
  typeof window !== 'undefined' ? window.localStorage : undefined;

/** Namespaced ID-token claim set by the post-login attribution Action. */
export const FIRST_LOGIN_CLAIM = 'https://tryiris.ai/first_login';

// Auth0 caps ext-* values at 255 characters (see AUTH0_EXT_VALUE_PATTERN).
const ATTRIBUTION_VALUE_MAX_LENGTH = 255;

/**
 * Auth0 accepts an `ext-*` authorize value only when it matches this pattern;
 * it is the exact regex Auth0 quotes in its `invalid_request: error parsing
 * ext params` response. A landing URL with a query string (`?`, `=`, `&`), a
 * `%`-escaped campaign value or PostHog's `$direct` referrer all fail it, and
 * Auth0 then answers /authorize with HTTP 400 before the login page renders
 * (itg incident 2026-09-04, Auth0 tracking id 837e8ba1fc6670db102b). Exported
 * so tests can prove every value we put on the URL satisfies it.
 */
export const AUTH0_EXT_VALUE_PATTERN = /^[-\w.*~@+ /:]{1,255}$/;

/** Marker for values the pattern rejects; `~` is itself an allowed character. */
export const ENCODED_EXT_VALUE_PREFIX = '~';

// 255 total, minus the prefix, and base64url turns 3 bytes into 4 characters:
// floor((255 - 1) / 4) * 3 = 189 raw bytes.
const ENCODED_EXT_RAW_MAX_BYTES = 189;

/**
 * Auth0 also caps the NUMBER of `ext-*` params on one authorize request:
 * `invalid_request: error parsing ext params: must have less than or equal to
 * 10 keys with "ext-" prefix` (itg, 2026-09-04, right after the charset fix).
 * The hosted Universal Login template reads nine of them, so attribution gets
 * exactly one key: ATTRIBUTION_EXT_KEY, a packed JSON object.
 */
export const AUTH0_MAX_EXT_KEYS = 10;

/** The single ext-* key that carries every attribution field, packed as JSON. */
export const ATTRIBUTION_EXT_KEY = 'ext-attribution';

// Per-field cap before packing. The whole JSON object has to fit in the 189
// raw bytes that survive "~"+base64url inside Auth0's 255-char value cap, so a
// single field is bounded well below that; utm values are short in practice.
const ATTRIBUTION_FIELD_MAX_LENGTH = 100;

// Packing priority when the budget is tight: campaign params first (they feed
// $initial_utm_* first-touch), click ids next, then referrer, landing URL last.
// Computed LAZILY: `#~/posthog/index.js` and this module import each other, so
// ATTRIBUTION_PARAMS is still uninitialised while this module evaluates. A
// top-level spread here threw "is not iterable" and took the itg app down at
// boot (2026-09-04, #1705). Nothing in this module may read a posthog export
// at evaluation time.
const attributionPackOrder = (): readonly string[] => [
  'as',
  ...(ATTRIBUTION_PARAMS ?? []),
  // GA client id after campaigns: without it the relay still counts the signup
  // under a synthetic id, only GA-side attribution is lost.
  'gc',
  'gs',
  'referrer',
  'landing_url'
];

// The `_ga` cookie is GA1.<n>.<random>.<timestamp>; GA's client_id is the last
// two parts. The relay sends it so the server sign_up joins the browser's GA user.
const readGACookies = (): string => {
  try {
    return typeof document === 'undefined' ? '' : document.cookie;
  } catch {
    // Privacy/sandbox restrictions on optional analytics must not block login.
    return '';
  }
};

export const readGAClientId = (): string | undefined => {
  const match = /(?:^|;\s*)_ga=GA\d+\.\d+\.(\d{1,20}\.\d{1,20})(?:;|$)/.exec(readGACookies());
  return match?.[1];
};

// GA keeps one session per stream, in `_ga_<stream id without "G-">`. The relay
// sends each vendor's signups to that vendor's production stream
// (approvedProductionStream in signupdelivery/fanout.go), so this reads that
// stream's session. Formats: GS1.1.<session>.<n>... and GS2.1.s<session>$o<n>...
const RELAY_GA_STREAM: Record<typeof config.vendor, string> = {
  iris: '0MY276EN9H',
  ptzoptics: 'MLKQK38NG7'
};

export const readGASessionId = (): string | undefined => {
  const stream = RELAY_GA_STREAM[config.vendor];
  if (typeof document === 'undefined' || !stream) return undefined;
  const match = new RegExp(`(?:^|;\\s*)_ga_${stream}=GS\\d\\.\\d\\.s?(\\d{1,20})(?:[.$;]|$)`).exec(
    readGACookies()
  );
  return match?.[1];
};

const toBase64Url = (bytes: Uint8Array): string => {
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/**
 * Make an attribution value safe for an Auth0 `ext-*` param. Values the
 * pattern already accepts travel verbatim (so utm_source stays readable in
 * Auth0 logs); anything else is sent as `~` + base64url of its UTF-8 bytes,
 * bounded so the result never exceeds Auth0's 255. The post-login Action
 * (auth0/actions/post-login-attribution.js, decodeExtValue) reverses this.
 * Per rule: verify-protocol-before-implementing (ai.knowledge/global): the
 * pattern is Auth0's own, not an assumption.
 */
export const encodeAuth0ExtValue = (value: string): string => {
  const bounded = value.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH);
  if (AUTH0_EXT_VALUE_PATTERN.test(bounded)) {
    return bounded;
  }
  const bytes = new TextEncoder().encode(value).slice(0, ENCODED_EXT_RAW_MAX_BYTES);
  return `${ENCODED_EXT_VALUE_PREFIX}${toBase64Url(bytes)}`;
};

const registrationCompletedTracker = createAuthJourneyTracker((name, category, properties) => {
  void trackAnalytics({ category, name, properties } as AnalyticsEvent);
});

const readLiveUrlAttribution = (): Record<string, string> => {
  if (typeof window === 'undefined') {
    return {};
  }

  const attribution: Record<string, string> = {};
  const params = new URLSearchParams(window.location.search);

  for (const name of ATTRIBUTION_PARAMS) {
    const value = params.get(name);
    if (value) {
      if (!name.startsWith('utm_') && value.length > 256) {
        attribution.ac = '0';
        attribution.ck ??= name;
        continue;
      }
      attribution[name] = value.slice(0, 256);
    }
  }

  if (window.location.href) {
    attribution.landing_url = window.location.href.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH);
  }
  if (typeof document !== 'undefined' && document.referrer) {
    attribution.referrer = document.referrer.slice(0, ATTRIBUTION_VALUE_MAX_LENGTH);
  }

  return attribution;
};

const getAttributionValues = (): Record<string, string> => {
  const stored = readSignupAttribution();
  return Object.keys(stored).length > 0 ? stored : readLiveUrlAttribution();
};

/**
 * Pack attribution into ONE Auth0 ext-* value. Fields are added in
 * ATTRIBUTION_PACK_ORDER while the JSON still fits the encoded budget, so a
 * long landing URL is what gets dropped, never utm_source. Returns null when
 * nothing fits or nothing was present. The post-login Action
 * (auth0/actions/post-login-attribution.js) JSON-parses the decoded value.
 */
const referrerOrigin = (referrer: string): string | undefined => {
  try {
    const { origin } = new URL(referrer);
    return origin === 'null' ? undefined : origin;
  } catch {
    return undefined;
  }
};

export const packAttribution = (values: Record<string, string>): string | null => {
  // Reserve the completeness marker before applying the byte budget. Missing
  // fields in older or truncated packs cannot prove an absent first touch.
  const packed: Record<string, string> = { ac: values.ac === '0' ? '0' : '1' };
  // Preserve only the allowlisted kind of an opaque click ID if its value
  // cannot fit. The Action can classify its network without inventing an ID.
  const clickKey = (ATTRIBUTION_PARAMS ?? []).find(
    (key) => !key.startsWith('utm_') && (values[key] || values.ck === key)
  );
  if (clickKey) packed.ck = clickKey;
  const encoder = new TextEncoder();
  for (const name of attributionPackOrder()) {
    const raw = values[name];
    // A GA session means nothing without its client id, which may not have fit.
    if (!raw || (name === 'gs' && !packed.gc)) {
      continue;
    }
    // Click identifiers are opaque: a prefix is a different, invalid ID.
    // Send them whole or omit them; readable campaign values may be bounded.
    const opaque = ATTRIBUTION_PARAMS?.some((key) => key === name) && !name.startsWith('utm_');
    const bounded = opaque ? raw : raw.slice(0, ATTRIBUTION_FIELD_MAX_LENGTH);
    const fits = (value: string) =>
      encoder.encode(JSON.stringify({ ...packed, [name]: value })).length <=
      ENCODED_EXT_RAW_MAX_BYTES;
    if (fits(bounded)) {
      packed[name] = bounded;
    } else if (name === 'referrer') {
      // The Action classifies organic traffic by the referrer's host and only
      // honours a preserved first touch it can see evidence for, so a referrer
      // that does not fit whole is packed as its origin rather than dropped.
      const origin = referrerOrigin(bounded);
      if (origin && fits(origin)) packed[name] = origin;
    }
  }
  if (clickKey && packed[clickKey]) delete packed.ck;
  if (Object.keys(packed).length === 1) {
    return null;
  }
  if (
    !ATTRIBUTION_PARAMS ||
    ATTRIBUTION_PARAMS.some((name) => values[name] && packed[name] !== values[name])
  )
    packed.ac = '0';
  // JSON always contains `{"`, so this is always the "~"+base64url form.
  return encodeAuth0ExtValue(JSON.stringify(packed));
};

export const getAttributionExtParams = (journeyId?: string): Record<string, string> => {
  // Same kill-switch as getPostHogExtParams: capture-off must not put
  // attribution on the authorize URL while withholding the session ids.
  if (typeof window === 'undefined' || !config.posthogApiKey || !isHostedPageTrackingEnabled()) {
    return {};
  }

  // One key, not one per field: Auth0 allows AUTH0_MAX_EXT_KEYS ext-* params
  // in total and the hosted template already needs nine of them.
  const values = { ...getAttributionValues() };
  // Desktop internal routes are not marketing landing pages. Preserve explicit
  // campaign data, but never manufacture web provenance from file:// or app URLs.
  if (isElectronApp()) {
    delete values.landing_url;
    delete values.referrer;
    values.attribution_status = 'unavailable';
  } else {
    const gaClientId = readGAClientId();
    if (gaClientId) {
      values.gc = gaClientId;
      // The relay attaches the server sign_up to this GA session; a session
      // means nothing without its client id, so it is only packed with one.
      const gaSessionId = readGASessionId();
      if (gaSessionId) values.gs = gaSessionId;
    }
  }
  // The short wire key reserves provenance inside the existing 189-byte pack
  // rather than adding an eleventh ext-* key or dropping it behind a long URL.
  values.as =
    values.attribution_status === 'direct'
      ? 'd'
      : values.attribution_status === 'preserved' || ATTRIBUTION_PARAMS.some((name) => values[name])
        ? 'p'
        : 'u';
  const packed = packAttribution(values);
  if (packed && journeyId) {
    // One bounded snapshot, scoped to this browser's current signup journey.
    // Storage restrictions must never interrupt the authorize redirect.
    try {
      const campaign = Object.fromEntries(
        ATTRIBUTION_PARAMS.flatMap((key) => {
          const value = values[key];
          if (!value || (!key.startsWith('utm_') && value.length > 256)) return [];
          return [[key, value.slice(0, 256)]];
        })
      );
      // A bounded snapshot cannot assert absence for a click ID it omitted.
      if (
        values.ac === '0' ||
        ATTRIBUTION_PARAMS.some((key) => values[key] && campaign[key] !== values[key])
      )
        campaign.ac = '0';
      writeStorage(
        safeSessionStorage(),
        SIGNUP_ATTRIBUTION_SNAPSHOT_KEY,
        JSON.stringify({ journeyId, createdAt: Date.now(), campaign })
      );
    } catch {
      /* optional recovery only */
    }
  }
  return packed ? { [ATTRIBUTION_EXT_KEY]: packed } : {};
};

const signupAttributionSnapshot = (journeyId: unknown): Record<string, string> | undefined => {
  if (typeof journeyId !== 'string' || !journeyId) return;
  try {
    const raw = readStorage(safeSessionStorage(), SIGNUP_ATTRIBUTION_SNAPSHOT_KEY);
    const snapshot = raw ? JSON.parse(raw) : undefined;
    const age = Date.now() - snapshot?.createdAt;
    if (
      snapshot?.journeyId !== journeyId ||
      !Number.isFinite(age) ||
      age < 0 ||
      age > ACTIVE_AUTH_JOURNEY_TTL_MS ||
      !snapshot?.campaign ||
      typeof snapshot.campaign !== 'object'
    )
      return;
    return {
      ...(snapshot.campaign.ac === '0' ? { ac: '0' } : {}),
      ...Object.fromEntries(
        ATTRIBUTION_PARAMS.filter((key) => typeof snapshot.campaign[key] === 'string').map(
          (key) => [key, snapshot.campaign[key].slice(0, 256)]
        )
      )
    };
  } catch {
    return;
  }
};

const getPostHogExtParams = (): Record<string, string> => {
  // Same kill-switch check as isHostedPageTrackingEnabled: these ids exist to
  // stitch the hosted page's own captures onto this session, so handing them
  // over while capture is off would be linking sends that must not happen.
  if (typeof window === 'undefined' || !config.posthogApiKey || !isHostedPageTrackingEnabled()) {
    return {};
  }

  const { distinctId, sessionId } = PostHogManager.getInstance().getSessionLinkingIds() ?? {};

  return {
    ...(distinctId ? { 'ext-posthog_distinct_id': distinctId } : {}),
    ...(sessionId ? { 'ext-posthog_session_id': sessionId } : {})
  };
};

const createJourneyId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const resolvePlatform = (): 'web' | 'electron' => (isElectronApp() ? 'electron' : 'web');

const isAuthJourneyExpired = (journey: Pick<AuthJourneyStateTransport, 'createdAt'>): boolean =>
  Date.now() - (journey.createdAt ?? 0) > ACTIVE_AUTH_JOURNEY_TTL_MS;

const isValidAuthJourney = (value: unknown): value is AuthJourneyStateTransport => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const journey = value as Partial<AuthJourneyStateTransport>;

  return (
    typeof journey.authJourneyId === 'string' &&
    journey.authJourneyId.length > 0 &&
    typeof journey.createdAt === 'number' &&
    Number.isFinite(journey.createdAt) &&
    typeof journey.vendor === 'string' &&
    typeof journey.env === 'string' &&
    (journey.platform === 'web' || journey.platform === 'electron') &&
    AUTH_JOURNEY_SOURCES.has(journey.source as AuthJourneySource)
  );
};

export interface AuthJourneyAppState extends AppState {
  returnTo?: string;
  authJourney?: AuthJourneyStateTransport;
  fromRegistration?: boolean;
}

export const beginAuthJourney = (source: AuthJourneySource): AuthJourneyStateTransport => {
  const journey: AuthJourneyStateTransport = {
    authJourneyId: createJourneyId(),
    createdAt: Date.now(),
    source,
    platform: resolvePlatform(),
    vendor: config.vendor ?? 'unknown',
    env: config.env
  };

  setActiveAuthJourney(journey);
  return journey;
};

export const setActiveAuthJourney = (journey: AuthJourneyStateTransport): void => {
  writeStorage(safeSessionStorage(), ACTIVE_AUTH_JOURNEY_KEY, JSON.stringify(journey));
};

export const clearActiveAuthJourney = (): void => {
  removeStorage(safeSessionStorage(), ACTIVE_AUTH_JOURNEY_KEY);
};

export const getActiveAuthJourney = (): AuthJourneyStateTransport | undefined => {
  const raw = readStorage(safeSessionStorage(), ACTIVE_AUTH_JOURNEY_KEY);

  if (!raw) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as AuthJourneyStateTransport;

    if (!isValidAuthJourney(parsed)) {
      return undefined;
    }

    if (isAuthJourneyExpired(parsed)) {
      clearActiveAuthJourney();
      return undefined;
    }

    return parsed;
  } catch {
    return undefined;
  }
};

export type AuthJourneyTerminalOutcome = 'succeeded' | 'failed';

/**
 * Record that a sign-in attempt reached a terminal outcome. Returns `true` only
 * for the first claim on a given journey — later claims, whatever their
 * outcome, get `false`.
 *
 * Callers use this to decide whether an observed outcome is countable. One
 * attempt can hand its outcome to the app repeatedly: the web callback
 * auto-opens the outcome deep link and the "Open Iris" button re-opens the same
 * url, and a re-delivered (already consumed) authorization code comes back as
 * an `invalid_grant` failure for a sign-in that actually succeeded. The journey
 * id is the attempt id — a retry begins a new journey — so gating on this claim
 * counts attempts rather than deliveries, which a time-window debounce cannot
 * do without also swallowing a genuine second attempt.
 *
 * Only the most recent journey is retained: an outcome is always attributed to
 * the active journey, so an older one can never be the subject of a claim.
 */
export const claimAuthJourneyTerminal = (
  authJourneyId: string,
  outcome: AuthJourneyTerminalOutcome
): boolean => {
  const raw = readStorage(safeSessionStorage(), AUTH_JOURNEY_TERMINAL_KEY);

  if (raw) {
    try {
      const claimed = JSON.parse(raw) as { authJourneyId?: unknown };

      if (claimed.authJourneyId === authJourneyId) {
        return false;
      }
    } catch {
      // An unreadable record must not silence a real failure — fall through and
      // overwrite it.
    }
  }

  writeStorage(
    safeSessionStorage(),
    AUTH_JOURNEY_TERMINAL_KEY,
    JSON.stringify({ authJourneyId, outcome })
  );

  return true;
};

export const resolveAuthJourneyForRoute = (
  expectedSource: AuthJourneySource,
  locationState?: LocationState
): AuthJourneyStateTransport => {
  const locationJourney = locationState?.authJourney;

  if (isValidAuthJourney(locationJourney) && !isAuthJourneyExpired(locationJourney)) {
    return locationJourney;
  }

  const activeJourney = getActiveAuthJourney();

  if (activeJourney && activeJourney.source === expectedSource) {
    return activeJourney;
  }

  return beginAuthJourney(expectedSource);
};

export const setLastKnownUserId = (userId: User['id']): void => {
  writeStorage(safeLocalStorage(), LAST_KNOWN_USER_ID_KEY, userId);
};

export const getLastKnownUserId = (): string | undefined =>
  readStorage(safeLocalStorage(), LAST_KNOWN_USER_ID_KEY);

// Runs inside the synchronous login redirect, so a broken SDK must not throw
// out of it; an unreadable opt-out state counts as opted out.
const isSdkOptedOut = (): boolean => {
  try {
    const sdk = PostHogManager.getInstance().getPostHog?.();
    if (sdk?.has_opted_out_capturing) return sdk.has_opted_out_capturing() === true;
    // Register redirects can precede asynchronous SDK initialization. Match the
    // installed posthog-js ConsentManager's default key and localStorage-first
    // persistence (including its legacy cookie fallback), without changing it.
    // Studio does not override the SDK consent persistence options.
    if (typeof window === 'undefined' || !config.posthogApiKey) return true;
    const key = `__ph_opt_in_out_${config.posthogApiKey}`;
    const stored = window.localStorage.getItem(key);
    const cookie = document.cookie
      .split(';')
      .map((part) => part.trim())
      .find((part) => part.startsWith(`${key}=`));
    const raw = stored ?? (cookie ? decodeURIComponent(cookie.slice(key.length + 1)) : null);
    // Absent consent retains the existing default. Unreadable or malformed
    // persisted consent fails closed instead of becoming a new enrollment.
    return raw !== null && !['1', 'true', 'yes'].includes(raw.toLowerCase().trim());
  } catch {
    return true;
  }
};

export const isHostedPageTrackingEnabled = (): boolean => {
  // The kill-switch has to reach here too. Off mode deliberately keeps a real
  // capture key so /flags still resolves (config/utils.ts pins keyEnv to itg),
  // so "is there a key" is not a usable gate: without this check an off-mode
  // boot still hands the live key to the Auth0 hosted page, which captures on
  // its own origin, outside every seam the app can observe.
  if (config.posthogCaptureDisabled) return false;
  if (isSdkOptedOut()) return false;
  const raw = readStorage(safeLocalStorage(), DISABLE_HOSTED_PAGE_TRACKING_KEY);
  return raw !== 'true' && raw !== '1';
};

export const buildHostedAuth0ExtParams = (
  journey: AuthJourneyStateTransport
): Record<string, string> => {
  const params: Record<string, string> = {
    'ext-auth_journey_id': journey.authJourneyId,
    'ext-platform': journey.platform,
    'ext-vendor': journey.vendor,
    'ext-env': journey.env,
    'ext-source': journey.source,
    'ext-hosted_tracking': '0'
  };
  try {
    const trackingEnabled = isHostedPageTrackingEnabled();
    // Retain successfully read permission if optional identity/attribution
    // fails. With no capture key, the hosted browser stays silent while the
    // Action can still enroll the canonical signup without browser identity.
    params['ext-hosted_tracking'] = trackingEnabled ? '1' : '0';
    // Build optional tracking atomically: a failing SDK/storage read must not
    // interrupt any login route or leave a capture key with a partial payload.
    Object.assign(params, {
      'ext-hosted_tracking': trackingEnabled ? '1' : '0',
      ...(config.posthogApiKey && trackingEnabled
        ? { 'ext-posthog_key': config.posthogApiKey }
        : {}),
      // The hosted page defaults its host; that ext slot belongs to attribution.
      ...getPostHogExtParams(),
      ...getAttributionExtParams(journey.authJourneyId)
    });
  } catch {
    // Optional analytics fails closed; the required authentication handoff stays usable.
  }

  // Auth0 fails the WHOLE authorize request over the cap. Attribution is the
  // only optional passenger here, so it is what gets dropped, never the login.
  if (Object.keys(params).length > AUTH0_MAX_EXT_KEYS) {
    delete params[ATTRIBUTION_EXT_KEY];
  }
  return params;
};

const SIGNUP_IDENTITY_CLAIM = 'https://tryiris.ai/signup_identity';
const REGISTRATION_EVENT_ID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-5[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

// Latch keys whose bridge capture has not settled yet.
const bridgesInFlight = new Set<string>();
const bridgesQueuedThisBoot = new Set<string>();

// Only the signed, Action-derived fallback identity authorizes this merge.
const signupIdentity = (auth0User: Auth0User | undefined) => {
  const identity = auth0User?.[SIGNUP_IDENTITY_CLAIM];
  if (
    identity?.version !== 2 ||
    typeof identity.registration_event_id !== 'string' ||
    !REGISTRATION_EVENT_ID_PATTERN.test(identity.registration_event_id) ||
    identity.distinct_id !== `signup:${identity.registration_event_id}`
  ) {
    return;
  }
  return identity;
};

/** Supply the signed first touch before the SDK's initial identify can freeze a later campaign. */
export const getSignupIdentitySetOnce = (
  auth0User: Auth0User | undefined
): Record<string, string | null> => {
  const identity = signupIdentity(auth0User);
  // Returning users without the signed claim stay entirely outside recovery,
  // including its SDK/opt-out reads on the shared login path.
  if (!identity || !isHostedPageTrackingEnabled()) return {};
  const attribution = identity.attribution ?? {};
  // Only original transport loss may recover unavailable fields. Older recovery
  // claims that accidentally carried a later login's journey cannot authorize it.
  const snapshot =
    ['direct', 'preserved'].includes(attribution.attribution_status) ||
    identity.attribution_transport_omitted === true
      ? signupAttributionSnapshot(identity.auth_journey_id)
      : undefined;
  const status =
    snapshot && identity.attribution_transport_omitted === true && Object.keys(snapshot).length > 0
      ? 'preserved'
      : attribution.attribution_status;
  const setOnce: Record<string, string | null> = {};
  // posthog-js merges initial/session campaigns before our overrides and skips
  // undefined values. Its own absent campaign value is null, but absence is
  // known only from direct provenance, a complete pack, or the matching snapshot.
  // Per rule: verify-protocol-before-implementing (ai.knowledge/global) — tested
  // through the installed SDK; set_once never overwrites existing person fields.
  // An unavailable/missing claim is not evidence of a direct signup: retain
  // the browser's own observed initial campaign instead of freezing it as null.
  if (
    status === 'direct' ||
    (status === 'preserved' &&
      (snapshot ? snapshot.ac !== '0' : identity.attribution_complete === true))
  ) {
    for (const key of ATTRIBUTION_PARAMS) {
      setOnce[key] = null;
      setOnce[`$initial_${key}`] = null;
    }
  }
  // Use the same allowlist for the first identify and the fallback identity merge.
  // The matching browser snapshot predates transport bounding. Its complete
  // values supersede fragments in the signed pack, never another journey's data.
  for (const [key, value] of Object.entries({ ...attribution, ...snapshot })) {
    if (
      (/^utm_[a-z0-9_]{1,40}$/.test(key) ||
        ATTRIBUTION_PARAMS.some((allowed) => allowed === key)) &&
      typeof value === 'string'
    ) {
      setOnce[`$initial_${key}`] = value.slice(0, 256);
      setOnce[key] = value.slice(0, 256);
    }
  }
  if (['direct', 'preserved', 'unavailable'].includes(status)) {
    setOnce.initial_attribution_status = status;
  }
  return setOnce;
};

/** Link the personless server signup event to the registry user without replacing browser history. */
const maybeBridgeSignupIdentity = async (
  auth0User: Auth0User | undefined,
  userId: string
): Promise<void> => {
  const identity = signupIdentity(auth0User);
  if (!identity) return;
  // The signed claim is the durable retry authority for 30 days. The SDK does
  // not expose capture acknowledgement, so never persist a completion latch.
  // Repeat the idempotent merge on each boot; coalesce calls within this boot.
  const latchKey = identity.registration_event_id;
  const manager = PostHogManager.getInstance();
  // Capture drops events before the SDK boots; leave the latch unset to retry.
  if (
    bridgesInFlight.has(latchKey) ||
    bridgesQueuedThisBoot.has(latchKey) ||
    !manager.getPostHog?.()
  ) {
    return;
  }

  // Normal SDK identify above this call links the actual browser history.
  // This second identity event links ONLY the Action-derived anonymous source;
  // arbitrary query/browser ids are never used as merge authority.
  const setOnce = getSignupIdentitySetOnce(auth0User);
  // Options, not properties: posthog-js builds the event's top-level $set_once
  // from the browser's own $initial_* values and lets options.$set_once win.
  // Queue acceptance only coalesces this boot, never marks delivery complete.
  // A dropped capture stays retryable in this boot. The in-flight set stops a
  // second boot path from sending the same merge while Electron's capture waits.
  bridgesInFlight.add(latchKey);
  try {
    const queued = await captureSignupIdentity(
      { distinct_id: userId, $anon_distinct_id: identity.distinct_id },
      { $set_once: setOnce }
    );
    if (queued) bridgesQueuedThisBoot.add(latchKey);
  } finally {
    // Both synchronous SDK throws and rejected promises release the latch.
    // Per rule: settle-every-terminal-branch (ai.knowledge/global).
    bridgesInFlight.delete(latchKey);
  }
};

/**
 * Fire `registration_completed` once per first-login user. The Action's
 * namespaced claim is a first-user signal (stays true for the whole first
 * session), so a localStorage latch is what keeps the event single-fire
 * across silent refreshes and reboots.
 *
 * Per rule: stay-in-scope (ai.knowledge/global) — one browser writer.
 */
export const maybeTrackRegistrationCompleted = (
  auth0User: Auth0User | undefined,
  userId: string | undefined
): void => {
  // Only the PostHog bridge honours hosted-page tracking; registration_completed
  // also feeds GA4/GTM and keeps its own provider gating.
  if (userId && signupIdentity(auth0User) && isHostedPageTrackingEnabled()) {
    // Signup recovery is optional: a broken SDK must never interrupt login or
    // the existing registration_completed providers below.
    void maybeBridgeSignupIdentity(auth0User, userId).catch(() => {});
  }
  if (!userId || auth0User?.[FIRST_LOGIN_CLAIM] !== true) {
    return;
  }

  const latchKey = `analytics.registration_completed_fired.${userId}`;
  if (readStorage(safeLocalStorage(), latchKey)) {
    return;
  }

  writeStorage(safeLocalStorage(), latchKey, '1');
  registrationCompletedTracker.trackRegistrationCompleted(getActiveAuthJourney());
};

export const makeAuthJourneyAppState = (
  returnTo: string,
  journey: AuthJourneyStateTransport
): AuthJourneyAppState => ({
  returnTo,
  authJourney: journey,
  fromRegistration: journey.source === 'studio_register'
});

export const getAuthJourneyFromAppState = (
  state?: AppState
): AuthJourneyStateTransport | undefined => {
  const appState = state as AuthJourneyAppState | undefined;
  const journey = appState?.authJourney;

  if (!journey?.authJourneyId) {
    return undefined;
  }

  return journey;
};
